import axios from 'axios';

const hostname = window.location.hostname;
const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
const PROD_API = 'https://apinew.jantakadarbar.com/api';
const API_BASE = process.env.REACT_APP_API_URL || (isLocal ? 'http://localhost:5000/api' : (hostname.includes('jantakadarbar.com') ? PROD_API : `http://${hostname}:5000/api`));

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken });
          localStorage.setItem('token', data.data.accessToken);
          localStorage.setItem('refreshToken', data.data.refreshToken);
          originalRequest.headers.Authorization = `Bearer ${data.data.accessToken}`;
          return api(originalRequest);
        } catch {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export const voiceAPI = {
  startConversation: (slug, visitorId) =>
    api.post('/voice/start', { companySlug: slug, visitorId }),
  sendMessage: (sessionId, message) =>
    api.post('/voice/message', { sessionId, message }),
  endConversation: (sessionId) =>
    api.post('/voice/end', { sessionId }),
};

export const authAPI = {
  companyLogin: (email, password) =>
    api.post('/auth/company/login', { email, password }),
  companyRegister: (data) =>
    api.post('/auth/company/register', data),
  refreshToken: (refreshToken) =>
    api.post('/auth/refresh', { refreshToken }),
};

export const companyAPI = {
  getProfile: () => api.get('/company/profile'),
  updateProfile: (data) => api.put('/company/profile', data),
  getUsers: (params) => api.get('/company/users', { params }),
  createUser: (data) => api.post('/company/users', data),
  updateUser: (id, data) => api.put(`/company/users/${id}`, data),
  deleteUser: (id) => api.delete(`/company/users/${id}`),
  getSubscription: () => api.get('/company/subscription'),
};

export const knowledgeAPI = {
  list: (params) => api.get('/knowledge', { params }),
  upload: (formData) => api.post('/knowledge/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  get: (id) => api.get(`/knowledge/${id}`),
  update: (id, data) => api.put(`/knowledge/${id}`, data),
  delete: (id) => api.delete(`/knowledge/${id}`),
  reindex: (id) => api.post(`/knowledge/${id}/reindex`),
  search: (q) => api.get('/knowledge/search', { params: { q } }),
};

export const conversationAPI = {
  list: (params) => api.get('/conversations', { params }),
  get: (id) => api.get(`/conversations/${id}`),
  delete: (id) => api.delete(`/conversations/${id}`),
  getStats: () => api.get('/conversations/stats'),
  export: (params) => api.get('/conversations/export', { params }),
};

export const leadAPI = {
  list: (params) => api.get('/leads', { params }),
  get: (id) => api.get(`/leads/${id}`),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  getStats: () => api.get('/leads/stats'),
  export: (params) => api.get('/leads/export', { params }),
};

export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  updatePrompt: (data) => api.put('/settings/prompt', data),
  updateVoice: (data) => api.put('/settings/voice', data),
  getPublic: (slug) => api.get(`/settings/public/${slug}`),
};

export const analyticsAPI = {
  getDashboard: (params) => api.get('/analytics/dashboard', { params }),
  getTimeSeries: (params) => api.get('/analytics/timeseries', { params }),
  getPopularQuestions: (params) => api.get('/analytics/popular-questions', { params }),
  getFullReport: () => api.get('/analytics/full-report'),
};

export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getSystemStats: () => api.get('/admin/system-stats'),
  listCompanies: (params) => api.get('/admin/companies', { params }),
  getCompany: (id) => api.get(`/admin/companies/${id}`),
  updateCompany: (id, data) => api.put(`/admin/companies/${id}`, data),
  deleteCompany: (id) => api.delete(`/admin/companies/${id}`),
  listAdmins: () => api.get('/admin/admins'),
  createAdmin: (data) => api.post('/admin/admins', data),
  updateAdmin: (id, data) => api.put(`/admin/admins/${id}`, data),
  deleteAdmin: (id) => api.delete(`/admin/admins/${id}`),
};

export default api;
